import React, { useState } from 'react';
import { Save, Loader2, CheckCircle, ArrowLeft, ArrowRight, Bell, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WizardSidebar, { SidebarSection, QAItem } from './WizardSidebar';
import QAAssistantPanel from './QAAssistantPanel';
import CaseFilesModal from './CaseFilesModal';

interface WizardV2LayoutProps {
  children: React.ReactNode;
  
  // Step info
  currentStep: number;
  highestCompletedStep?: number; // NEW: Track the furthest step the user has reached
  totalSteps?: number;
  stepTitle: string;
  stepSubtitle?: string;
  
  // Progress
  completionPercent: number;
  grade?: 'A' | 'B' | 'C' | 'D' | 'F';
  
  // Sidebar content
  sections?: SidebarSection[];
  qaItems?: QAItem[];
  nextAction?: {
    label: string;
    description?: string;
    onClick?: () => void;
  };
  
  // Mode
  calculationMode?: 'standard_bi' | 'future_closure';
  
  // Save state
  isSaving?: boolean;
  lastSaved?: Date | null;
  onSave?: () => void;
  
  // Navigation
  onBack?: () => void;
  onNext?: () => void;
  goToStep?: (step: number) => void;
  nextLabel?: string;
  backLabel?: string;
  showBack?: boolean;
  showNext?: boolean;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  
  // Deep linking support
  onSectionClick?: (stepNumber: number, sectionId: string) => void;
  activeSection?: string;
  urlSection?: string | null;
  
  // Quick Actions support
  onQuickAction?: (action: string) => void;
  
  // QA Assistant support
  claimId?: string;
  anomalyCount?: number;
  reviewCount?: number;
}

export default function WizardV2Layout({
  children,
  currentStep,
  highestCompletedStep,
  totalSteps = 7,
  stepTitle,
  stepSubtitle,
  completionPercent,
  grade,
  sections = [],
  qaItems = [],
  nextAction,
  calculationMode = 'standard_bi',
  isSaving = false,
  lastSaved,
  onSave,
  onBack,
  onNext,
  goToStep,
  nextLabel = 'Continue',
  backLabel = 'Back',
  showBack = true,
  showNext = true,
  isNextDisabled = false,
  isNextLoading = false,
  onSectionClick,
  activeSection,
  urlSection,
  onQuickAction,
  claimId,
  anomalyCount = 0,
  reviewCount = 0,
}: WizardV2LayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [qaAssistantOpen, setQaAssistantOpen] = useState(false);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get claimId from URL if not provided
  const effectiveClaimId = claimId || searchParams.get('draft') || localStorage.getItem('current_draft_id') || '';

  // Handle quick actions - intercept 'files' action
  const handleQuickAction = (action: string) => {
    if (action === 'files') {
      setFilesModalOpen(true);
    } else if (onQuickAction) {
      onQuickAction(action);
    }
  };

  // Format last saved time
  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const seconds = Math.floor((new Date().getTime() - lastSaved.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div className="h-screen flex bg-white">
      {/* Sidebar */}
      <WizardSidebar
        currentStep={currentStep}
        highestCompletedStep={highestCompletedStep}
        totalSteps={totalSteps}
        completionPercent={completionPercent}
        grade={grade}
        sections={sections}
        qaItems={qaItems}
        nextAction={nextAction}
        calculationMode={calculationMode}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        goToStep={goToStep}
        onSectionClick={onSectionClick}
        activeSection={activeSection}
        urlSection={urlSection}
        onQuickAction={handleQuickAction}
        onOpenQAAssistant={() => setQaAssistantOpen(true)}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        {/* Top header bar - matching dashboard exactly */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
          {/* Left side - Title and subtitle like dashboard */}
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{stepTitle}</h1>
            {stepSubtitle && (
              <p className="text-sm text-gray-500">{stepSubtitle}</p>
            )}
          </div>

          {/* Right side - Actions like dashboard */}
          <div className="flex items-center gap-3">
            {/* Save status */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Saved{lastSaved ? ` ${formatLastSaved()}` : ''}</span>
                </>
              )}
            </div>

            {/* Divider */}
            <div className="h-8 w-px bg-gray-200" />

            {/* Save button - styled like dashboard buttons */}
            {onSave && (
              <button
                onClick={onSave}
                disabled={isSaving}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Save"
              >
                <Save className="w-5 h-5" />
              </button>
            )}

            {/* Notification bell */}
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors relative">
              <Bell className="w-5 h-5" />
            </button>

            {/* Step indicator badge - like "Owner" badge in dashboard */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
              <span className="text-sm font-medium text-gray-700">Step {currentStep}/{totalSteps}</span>
            </div>
          </div>
        </header>

        {/* Content area - FULL WIDTH to maximize screen real estate */}
        <main className="flex-1 overflow-y-auto bg-white">
          <div className="h-full">
            {children}
          </div>
        </main>

        {/* Bottom navigation bar */}
        <footer className="h-16 bg-white border-t border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
          <div>
            {showBack && onBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {backLabel}
              </button>
            )}
          </div>

          <div>
            {showNext && onNext && (
              <button
                onClick={onNext}
                disabled={isNextDisabled || isNextLoading}
                className={`
                  flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all
                  ${isNextDisabled || isNextLoading
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                  }
                `}
              >
                {isNextLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {nextLabel}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </footer>
      </div>
      
      {/* Case Files Modal */}
      <CaseFilesModal
        isOpen={filesModalOpen}
        onClose={() => setFilesModalOpen(false)}
        claimId={effectiveClaimId}
      />

      {/* QA Assistant Panel */}
      <QAAssistantPanel
        isOpen={qaAssistantOpen}
        onClose={() => setQaAssistantOpen(false)}
        claimId={effectiveClaimId}
        currentStep={currentStep}
        anomalyCount={anomalyCount}
        reviewCount={reviewCount}
        onNavigate={(target) => {
          // Handle navigation from QA Assistant
          if (onSectionClick && currentStep === 3) {
            onSectionClick(3, target);
          } else if (onQuickAction) {
            onQuickAction(target);
          }
          setQaAssistantOpen(false);
        }}
      />
    </div>
  );
}
